import { escapeHtml } from "./reportModel";

function partyRow(label, name, email) {
  if (!name && !email) return "";
  return `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(name)}${
    email ? `<span class="muted"> · ${escapeHtml(email)}</span>` : ""
  }</td></tr>`;
}

function timelineHtml(model) {
  if (!model.timeline.length) {
    return `<p class="muted">No notes were recorded in this file.</p>`;
  }
  return `<ol class="timeline">${model.timeline
    .map(
      (item) => `<li>
        <header><strong>${escapeHtml(item.senderName)}</strong><time>${escapeHtml(item.stamp)}</time></header>
        <p>${escapeHtml(item.text).replace(/\n/g, "<br />")}</p>
        ${
          item.attachments?.length
            ? `<ul class="files">${item.attachments
                .map((file) => `<li>${escapeHtml(file.file_name || "Attachment")}</li>`)
                .join("")}</ul>`
            : ""
        }
      </li>`
    )
    .join("")}</ol>`;
}

function attachmentsHtml(model) {
  if (!model.attachments.length) {
    return `<p class="muted">No attachments were stored with this file.</p>`;
  }
  return `<ul class="files">${model.attachments
    .map(
      (file) =>
        `<li><strong>${escapeHtml(file.name)}</strong>${
          file.stamp ? ` <span class="muted">${escapeHtml(file.stamp)}</span>` : ""
        }${file.type ? ` <span class="muted">${escapeHtml(file.type)}</span>` : ""}</li>`
    )
    .join("")}</ul>`;
}

function checklistHtml(model) {
  const heading =
    model.checklistHeading ||
    (model.checklistType === "seller" ? "Seller portfolio summary" : "Buyer checklist summary");
  if (!model.checklistSections.length) {
    return `<h2>${heading}</h2><p class="muted">No checklist notes were saved for this file.</p>`;
  }
  return `<h2>${heading}</h2>${model.checklistSections
    .map(
      (section) => `<section class="block">
        <h3>${escapeHtml(section.title)}</h3>
        <ul>${section.lines.map((line) => `<li>${escapeHtml(line)}</li>`).join("")}</ul>
      </section>`
    )
    .join("")}`;
}

function listBlock(title, items, empty, render) {
  if (!items?.length) return `<h2>${escapeHtml(title)}</h2><p class="muted">${escapeHtml(empty)}</p>`;
  return `<h2>${escapeHtml(title)}</h2>${render(items)}`;
}

function packetHtml(model) {
  if (!model.packet) return "";
  const notes = listBlock(
    "Notes and tips",
    [
      ...(model.specialNotes ? [`Special notes: ${model.specialNotes}`] : []),
      ...(model.pageNotes || []).map((note) => `${note.page}: ${note.text}`),
      ...(model.tips || []).map((tip) => `${tip.title} — ${tip.status}`),
    ],
    "No notes or tip acknowledgements were saved on this file.",
    (items) => `<ul>${items.map((line) => `<li>${escapeHtml(line)}</li>`).join("")}</ul>`
  );
  const milestones = listBlock(
    "Transaction timeline",
    model.transactionMilestones,
    "No transaction milestones are on this file yet.",
    (items) =>
      `<ul>${items
        .map(
          (item) =>
            `<li><strong>${escapeHtml(item.title)}</strong> — ${escapeHtml(item.status)}${
              item.detail ? `<div class="muted">${escapeHtml(item.detail)}</div>` : ""
            }</li>`
        )
        .join("")}</ul>`
  );
  const custom = listBlock(
    "Custom steps",
    model.customSteps,
    "No custom office steps were added.",
    (items) =>
      `<ul>${items
        .map(
          (item) =>
            `<li><strong>${escapeHtml(item.title)}</strong> — ${escapeHtml(item.status)}${
              item.date ? ` · ${escapeHtml(item.date)}` : ""
            }${item.description ? `<div>${escapeHtml(item.description)}</div>` : ""}${
              item.notes ? `<div class="muted">${escapeHtml(item.notes)}</div>` : ""
            }${
              item.attachments?.length
                ? `<div class="muted">${escapeHtml(item.attachments.join(", "))}</div>`
                : ""
            }</li>`
        )
        .join("")}</ul>`
  );
  const lifecycle = listBlock(
    "File lifecycle",
    model.lifecycle,
    "Lifecycle stages have not been updated.",
    (items) =>
      `<ol>${items
        .map(
          (item) =>
            `<li><strong>${escapeHtml(item.title)}</strong> — ${escapeHtml(item.status)}${
              item.notes ? `<div class="muted">${escapeHtml(item.notes)}</div>` : ""
            }</li>`
        )
        .join("")}</ol>`
  );
  const calendar = listBlock(
    "Calendar",
    model.calendarEvents,
    "No calendar events are tied to this file.",
    (items) =>
      `<ul>${items
        .map(
          (item) =>
            `<li><strong>${escapeHtml(item.title)}</strong> · ${escapeHtml(
              [item.when, item.type, item.location].filter(Boolean).join(" · ")
            )}${item.notes ? `<div class="muted">${escapeHtml(item.notes)}</div>` : ""}</li>`
        )
        .join("")}</ul>`
  );
  const actions = listBlock(
    "Action record",
    model.actionTimeline,
    "No office actions have been recorded yet.",
    (items) =>
      `<ol class="timeline">${items
        .map(
          (item) => `<li>
            <header><strong>${escapeHtml(item.title)}</strong><time>${escapeHtml(item.stamp)}</time></header>
            <p>${escapeHtml([item.source, item.text].filter(Boolean).join(" · "))}</p>
          </li>`
        )
        .join("")}</ol>`
  );
  return `${notes}${milestones}${custom}${lifecycle}${calendar}${actions}`;
}

export function renderReportHtml(model) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(model.subtitle)} — ${escapeHtml(model.property)}</title>
  <style>
    :root {
      --evergreen: #082419;
      --sandstone: #f4f0e8;
      --shimmer: #c7b27a;
      --ink: #111111;
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      color: var(--ink);
      background: var(--sandstone);
      font: 15px/1.55 "Source Serif Pro", Georgia, serif;
    }
    header.banner {
      background: var(--evergreen);
      color: #fdfcf4;
      padding: 1.75rem 2rem 1.4rem;
    }
    header.banner h1 {
      margin: 0;
      font-family: "Playfair Display", Georgia, serif;
      font-size: 1.65rem;
      font-weight: 600;
      color: #7fa9c9;
    }
    header.banner p { margin: 0.25rem 0 0; color: #fdfcf4; }
    main { max-width: 44rem; margin: 0 auto; padding: 1.5rem 1.25rem 2.5rem; }
    h2 {
      font-family: "Playfair Display", Georgia, serif;
      font-size: 1.2rem;
      border-bottom: 1px solid var(--shimmer);
      padding-bottom: 0.3rem;
    }
    h3 { font-size: 1rem; margin-bottom: 0.35rem; }
    table.parties { width: 100%; border-collapse: collapse; margin: 0.75rem 0 1.25rem; }
    table.parties th {
      text-align: left;
      width: 7rem;
      color: #5a2f27;
      font-weight: 600;
      padding: 0.25rem 0.5rem 0.25rem 0;
    }
    table.parties td { padding: 0.25rem 0; }
    .muted { color: var(--ink); }
    .disclaimer {
      font-size: 0.85rem;
      color: var(--ink);
      border-top: 1px solid var(--shimmer);
      padding-top: 0.85rem;
      margin-top: 2rem;
    }
    .timeline { list-style: none; margin: 0; padding: 0; }
    .timeline li {
      border-left: 2px solid var(--shimmer);
      padding: 0 0 1rem 1rem;
      margin-left: 0.35rem;
    }
    .timeline header { display: flex; justify-content: space-between; gap: 1rem; }
    .files { padding-left: 1.1rem; }
    @media print {
      body { background: #fff; }
      header.banner { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <header class="banner">
    <h1>${escapeHtml(model.title)}</h1>
    <p>${escapeHtml(model.subtitle)} · ${escapeHtml(model.property)}</p>
  </header>
  <main>
    <h2>File</h2>
    <table class="parties">
      <tr><th>Property</th><td>${escapeHtml(model.property)}</td></tr>
      <tr><th>Status</th><td>${escapeHtml(model.status)}${model.clientRole ? ` · ${escapeHtml(model.clientRole)}` : ""}</td></tr>
      <tr><th>Prepared</th><td>${escapeHtml(model.generatedAt)}${model.generatedBy ? ` · ${escapeHtml(model.generatedBy)}` : ""}${model.exportType ? ` · ${escapeHtml(model.exportType)} desk` : ""}</td></tr>
      ${partyRow("Broker", model.parties.broker, model.partyEmails.broker)}
      ${partyRow("Agent", model.parties.agent, model.partyEmails.agent)}
      ${partyRow("Client", model.parties.client, model.partyEmails.client)}
      ${model.clientPhone ? partyRow("Phone", model.clientPhone, "") : ""}
      ${model.clientAddress ? partyRow("Address", model.clientAddress, "") : ""}
    </table>
    <h2>Timeline</h2>
    ${timelineHtml(model)}
    <h2>Messages</h2>
    <p class="muted">${model.timeline.length} note${model.timeline.length === 1 ? "" : "s"} in this file, shown in the timeline above.</p>
    <h2>Attachments</h2>
    ${attachmentsHtml(model)}
    ${checklistHtml(model)}
    ${packetHtml(model)}
    <p class="disclaimer">${escapeHtml(model.disclaimer)}</p>
  </main>
</body>
</html>`;
}
